import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, PlugZap, CircleCheck, CircleX, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useSite } from "@/context/SiteContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SiteSettingsSheet = ({ open, onOpenChange }) => {
  const { activeSite, refreshSites, fetchHealth } = useSite();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (activeSite && open) {
      setForm({
        name: activeSite.name,
        production_domain: activeSite.production_domain,
        wp_base_url: activeSite.wp_base_url,
        wp_username: activeSite.wp_username,
        wp_app_password: "",
        environment: activeSite.environment,
        default_mode: activeSite.default_mode,
        notes: activeSite.notes || "",
      });
      setTestResult(null);
    }
  }, [activeSite, open]);

  if (!activeSite || !form) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.wp_app_password) delete payload.wp_app_password;
      await axios.patch(`${API}/sites/${activeSite.id}`, payload);
      await refreshSites();
      fetchHealth(activeSite.id, true);
      toast.success("Workspace settings saved.");
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/sites/${activeSite.id}/test-connection`);
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, error: "Request failed" });
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/sites/${activeSite.id}`);
      toast.success(`Workspace “${activeSite.name}” removed.`);
      await refreshSites();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not delete workspace.");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="site-settings-sheet" className="w-[420px] sm:max-w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-nunito font-extrabold text-[#383838]">Workspace Settings</SheetTitle>
          <SheetDescription className="text-[#6e6e6e]">
            {activeSite.name} · {activeSite.is_seed ? "seed workspace" : "workspace"}
          </SheetDescription>
        </SheetHeader>

        <div className="grid gap-3 py-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">Site Name</Label>
            <Input data-testid="settings-name-input" value={form.name} onChange={set("name")} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">Production Domain</Label>
            <Input data-testid="settings-domain-input" value={form.production_domain} onChange={set("production_domain")} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">WP Deploy URL</Label>
            <Input data-testid="settings-wp-url-input" value={form.wp_base_url} onChange={set("wp_base_url")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">WP Username</Label>
              <Input data-testid="settings-username-input" autoComplete="off" value={form.wp_username} onChange={set("wp_username")} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">App Password</Label>
              <Input data-testid="settings-password-input" type="password" autoComplete="new-password" placeholder="leave blank to keep" value={form.wp_app_password} onChange={set("wp_app_password")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">Environment</Label>
              <Select value={form.environment} onValueChange={set("environment")}>
                <SelectTrigger data-testid="settings-environment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staging">Staging</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-nunito font-bold uppercase tracking-wide text-[#383838]">Default Mode</Label>
              <Select value={form.default_mode} onValueChange={set("default_mode")}>
                <SelectTrigger data-testid="settings-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button type="button" variant="outline" data-testid="settings-test-connection-button" disabled={testing} onClick={runTest} className="rounded-[10px] font-nunito text-xs font-bold uppercase tracking-wide">
              {testing ? <Loader2 size={13} className="animate-spin" /> : <PlugZap size={13} />}
              Test Connection
            </Button>
            {testResult && (
              <span data-testid="settings-test-result" className={`flex items-center gap-1 text-xs ${testResult.ok ? "text-[#0050c0]" : "text-[#c25a00]"}`}>
                {testResult.ok ? <CircleCheck size={13} /> : <CircleX size={13} />}
                {testResult.ok ? `Connected as ${testResult.user}` : (testResult.error || "Failed").slice(0, 46)}
              </span>
            )}
          </div>

          <Button type="button" data-testid="settings-save-button" disabled={saving} onClick={save} className="mt-1 rounded-[10px] bg-[#0050c0] hover:bg-[#0343a0] text-white font-nunito font-extrabold uppercase tracking-wide text-xs">
            {saving ? <Loader2 size={13} className="animate-spin" /> : null}
            Save Settings
          </Button>

          {!activeSite.is_seed && (
            <>
              <Separator className="my-2" />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="outline" data-testid="settings-delete-button" className="rounded-[10px] border-[#ee2e24]/40 text-[#ee2e24] hover:bg-[#ee2e24]/5 hover:text-[#d92820] font-nunito text-xs font-bold uppercase tracking-wide">
                    <Trash2 size={13} /> Delete Workspace
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-[14px]">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-nunito font-extrabold text-[#383838]">Delete “{activeSite.name}”?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[#6e6e6e]">
                      Removes the workspace and its stored credentials from this app. The WordPress site itself is not touched.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="settings-delete-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="settings-delete-confirm" onClick={remove} className="bg-[#ee2e24] hover:bg-[#d92820] text-white">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
